import { richTextInlineToHtml, richTextToHtml } from '@edms/shared';
import { useEffect, useRef, type KeyboardEvent, type MouseEvent } from 'react';

/** Текст с форматированием (описание задачи). HTML собирает richTextToHtml — он всё экранирует. */
export function RichText({ text, className = '' }: { text: string | null | undefined; className?: string }) {
  if (!text) return null;
  return <div className={`prose rich ${className}`} dangerouslySetInnerHTML={{ __html: richTextToHtml(text) }} />;
}

/** Строка с форматированием (заголовок новости): только жирный, курсив, зачёркнутый и ссылки. */
export function RichInline({ text, className = '' }: { text: string | null | undefined; className?: string }) {
  if (!text) return null;
  return <span className={`rich ${className}`} dangerouslySetInnerHTML={{ __html: richTextInlineToHtml(text) }} />;
}

// ---------------------------------------------------------------------------
// Редактор: правка «как в Word», а хранится простая разметка (см. rich-text.ts в shared).
// ---------------------------------------------------------------------------

const SAFE_URL = /^(https?:\/\/|mailto:)/i;

/** Обрамляет текст метками, вынося пробелы по краям наружу: «** жирный**» не распознаётся. */
function wrap(inner: string, mark: string): string {
  const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner)!;
  return m[2] ? `${m[1]}${mark}${m[2]}${mark}${m[3]}` : inner;
}

function toMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/ /g, ' ');
  if (!(node instanceof HTMLElement)) return '';
  const inner = () => [...node.childNodes].map(toMarkdown).join('');
  const style = node.style;
  switch (node.tagName) {
    case 'BR':
      return '\n';
    case 'B':
    case 'STRONG':
      return wrap(inner(), '**');
    case 'I':
    case 'EM':
      return wrap(inner(), '*');
    case 'S':
    case 'STRIKE':
    case 'DEL':
      return wrap(inner(), '~~');
    case 'A': {
      const href = node.getAttribute('href') ?? '';
      const text = inner().replace(/\n/g, ' ').trim();
      return SAFE_URL.test(href) && text ? `[${text.replace(/[[\]]/g, '')}](${href})` : text;
    }
    case 'BLOCKQUOTE':
      return `\n${blockText(inner())
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n')}\n`;
    case 'UL':
    case 'OL':
      return `\n${[...node.children]
        .map((li, i) => `${node.tagName === 'UL' ? '-' : `${i + 1}.`} ${blockText(toMarkdown(li)).replace(/\n+/g, ' ')}`)
        .join('\n')}\n`;
    case 'DIV':
    case 'P':
    case 'H1':
    case 'H2':
    case 'H3':
      return `\n${inner()}\n`;
    case 'SPAN':
      // Браузер иногда оформляет жирный/курсив стилем, а не тегом.
      if (style.fontWeight === 'bold' || Number(style.fontWeight) >= 600) return wrap(inner(), '**');
      if (style.fontStyle === 'italic') return wrap(inner(), '*');
      return inner();
    default:
      return inner();
  }
}

function blockText(s: string): string {
  return s.replace(/^\n+|\n+$/g, '').replace(/\n{2,}/g, '\n');
}

export function htmlToRichText(root: HTMLElement): string {
  return [...root.childNodes]
    .map(toMarkdown)
    .join('')
    .split('\n')
    .map((l) => l.replace(/\s+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

type Cmd = { label: string; title: string; run: () => void; className?: string };

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  id,
  minHeight = 110,
  inline = false,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
  minHeight?: number;
  /** Одна строка (заголовок): без абзацев, цитат и списков; Enter не переносит строку. */
  inline?: boolean;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const last = useRef<string | null>(null);

  // Внешнее значение (загрузка, сброс после отправки) — перерисовываем; своё же не трогаем, иначе прыгнет курсор.
  useEffect(() => {
    if (ref.current && value !== last.current) {
      ref.current.innerHTML = inline ? richTextInlineToHtml(value) : richTextToHtml(value);
      last.current = value;
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const md = inline ? htmlToRichText(ref.current).replace(/\s*\n\s*/g, ' ') : htmlToRichText(ref.current);
    last.current = md;
    onChange(md);
  };
  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };
  const link = () => {
    const sel = window.getSelection();
    const selected = sel && !sel.isCollapsed ? sel.toString() : '';
    const range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const raw = window.prompt('Адрес ссылки', 'https://');
    if (!raw || raw === 'https://') return;
    const url = /^[a-z]+:/i.test(raw) ? raw.trim() : `https://${raw.trim()}`;
    if (!SAFE_URL.test(url)) return;
    ref.current?.focus();
    if (range) {
      sel!.removeAllRanges();
      sel!.addRange(range);
    }
    if (selected) exec('createLink', url);
    else exec('insertHTML', `<a href="${url.replace(/"/g, '&quot;')}">${url.replace(/</g, '&lt;')}</a>&nbsp;`);
  };
  const quote = () => {
    const inQuote = (() => {
      let n: Node | null = window.getSelection()?.anchorNode ?? null;
      while (n && n !== ref.current) {
        if (n instanceof HTMLElement && n.tagName === 'BLOCKQUOTE') return true;
        n = n.parentNode;
      }
      return false;
    })();
    exec('formatBlock', inQuote ? 'div' : 'blockquote');
  };

  const all: Array<Cmd & { block?: boolean }> = [
    { label: 'Ж', title: 'Жирный (Ctrl+B)', run: () => exec('bold'), className: 'rte-b' },
    { label: 'К', title: 'Курсив (Ctrl+I)', run: () => exec('italic'), className: 'rte-i' },
    { label: 'З', title: 'Зачёркнутый', run: () => exec('strikeThrough'), className: 'rte-s' },
    { label: '❝', title: 'Цитата', run: quote, block: true },
    { label: '•', title: 'Список', run: () => exec('insertUnorderedList'), block: true },
    { label: '1.', title: 'Нумерованный список', run: () => exec('insertOrderedList'), block: true },
    { label: '🔗', title: 'Ссылка (Ctrl+K)', run: link },
    { label: '⌫', title: 'Убрать форматирование', run: () => exec('removeFormat') },
  ];
  const cmds = inline ? all.filter((c) => !c.block) : all;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (inline && e.key === 'Enter') {
      e.preventDefault();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'л' || e.code === 'KeyK')) {
      e.preventDefault();
      link();
    }
  };

  return (
    <div className={`rte${inline ? ' rte-inline' : ''}`}>
      <div className="rte-bar" role="toolbar" aria-label="Форматирование">
        {cmds.map((c) => (
          <button
            key={c.title}
            type="button"
            className={`rte-btn ${c.className ?? ''}`}
            title={c.title}
            aria-label={c.title}
            // mousedown, а не click: иначе кнопка заберёт фокус и выделение пропадёт.
            onMouseDown={(e: MouseEvent) => {
              e.preventDefault();
              c.run();
            }}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div
        id={id}
        ref={ref}
        className="rte-area prose rich"
        contentEditable
        role="textbox"
        aria-multiline={!inline}
        aria-label={label}
        data-placeholder={placeholder}
        style={{ minHeight }}
        onInput={emit}
        onKeyDown={onKeyDown}
        onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'div')}
        onPaste={(e) => {
          // Вставка — простым текстом: оформление из Word и сайтов не тащим.
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, inline ? text.replace(/\s*\n\s*/g, ' ') : text);
          emit();
        }}
        suppressContentEditableWarning
      />
    </div>
  );
}
